import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    TextField,
    ClickAwayListener,
    Paper,
    Typography,
    Switch,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    Button,
    Tabs,
    Tab,
    Grid,
    useMediaQuery,
    Tooltip,
    Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PapiroConsole } from "src/utils/papiroConsole";
import { useTheme } from "@mui/styles";
import moment from "moment";
import SimpleDatePicker from 'src/components/date-pickers/inline-no-today'
import { Info } from "@mui/icons-material";
import Api from "src/services/api";

const GeneralSearchBar = (props) => {
    const { intl, history, setAlertNotification, usematerializedview } = props
    const tabs = [
        intl.formatMessage({ id: "called.header" }),
        intl.formatMessage({ id: "news" }),
        intl.formatMessage({ id: "faq" }),
        intl.formatMessage({ id: "knowledgebases" }),
        intl.formatMessage({ id: "menu.users" }),
    ]
    const [selectedTab, setSelectedTab] = useState(0)
    const settingsTabs = [
        {
            name: "general",
            label: intl.formatMessage({ id: "general" }),
            switches: [
                { name: "startdate", label: intl.formatMessage({ id: "start.date" }) },
                { name: "enddate", label: intl.formatMessage({ id: "end.date" }) },
            ]
        },
        {
            name: "calleds",
            label: intl.formatMessage({ id: "called.header" }),
            switches: [
                { name: "id", label: intl.formatMessage({ id: "id" }) },
                { name: "title", label: intl.formatMessage({ id: "title" }) },
                { name: "description", label: intl.formatMessage({ id: "description" }) },
                { name: "accompaniments", label: intl.formatMessage({ id: "accompaniments" }) },
                { name: "users", label: intl.formatMessage({ id: "menu.users" }) },
                { name: "attatchments", label: intl.formatMessage({ id: "attachments" }) },
                { name: "quicksearch", label: intl.formatMessage({ id: "quick.search" }) },
            ],
        },
        {
            name: "news",
            label: intl.formatMessage({ id: "news" }),
            switches: [
                { name: "title", label: intl.formatMessage({ id: "title" }) },
                { name: "subtitle", label: intl.formatMessage({ id: "subtitle" }) },
                { name: "content", label: intl.formatMessage({ id: "content" }) },
                { name: "articlesectionname", label: intl.formatMessage({ id: "section.name" }) },
                { name: "user", label: intl.formatMessage({ id: "user" }) },
                { name: "attatchments", label: intl.formatMessage({ id: "attachments" }) }
            ]
        },
        {
            name: "faq",
            label: intl.formatMessage({ id: "faq" }),
            switches: [
                { name: "question", label: intl.formatMessage({ id: "question" }) },
                { name: "answer", label: intl.formatMessage({ id: "response" }) },
                { name: "subjectname", label: intl.formatMessage({ id: "subject" }) },
            ]
        },
        {
            name: "knowledgebases",
            label: intl.formatMessage({ id: "knowledgebases" }),
            switches: [
                { name: "title", label: intl.formatMessage({ id: "title" }) },
                { name: "description", label: intl.formatMessage({ id: "description" }) },
                { name: "keywords", label: intl.formatMessage({ id: "keywords" }) },
                { name: "attatchments", label: intl.formatMessage({ id: "attachments" }) }
            ]
        },
        {
            name: "users",
            label: intl.formatMessage({ id: "menu.users" }),
            switches: [
                { name: "name", label: intl.formatMessage({ id: "name" }) },
                { name: "email", label: intl.formatMessage({ id: "email" }) },
                { name: "phone", label: intl.formatMessage({ id: "phone" }) },
                { name: "document", label: intl.formatMessage({ id: "document" }) }
            ],
        },
    ]
    const [activeTab, setActiveTab] = useState(0)
    const [open, setOpen] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [searchText, setSearchText] = useState("")
    const inputRef = useRef(null)
    const containerRef = useRef(null)

    const [alertType, setAlertType] = useState("info")
    const [alertMessage, setAlertMessage] = useState(intl.formatMessage({ id: "general.search.max.period" }))

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));


    const [searchPreferences, setSearchPreferences] = useState(
        localStorage.getItem("generalSearchPreferences") ? JSON.parse(localStorage.getItem("generalSearchPreferences")) :
            {
                general: {
                    startdate: moment().subtract(6, 'months').format('YYYY/MM/DD HH:mm:ss'),
                    enddate: moment().format('YYYY/MM/DD HH:mm:ss'),
                },
                calleds: {
                    id: true,
                    title: true,
                    description: false,
                    accompaniments: false,
                    users: false,
                    attatchments: false,
                    quicksearch: usematerializedview,
                },
                news: {
                    title: true,
                    subtitle: true,
                    content: true,
                    articlesectionname: true,
                    attatchments: true,
                    user: true
                },
                faq: {
                    question: true,
                    answer: true,
                    subjectname: true,
                },
                knowledgebases: {
                    title: true,
                    description: true,
                    keywords: true,
                    attatchments: true,
                },
                users: {
                    name: true,
                    email: true,
                    document: true,
                    phone: true,
                },
            })

    const handleClickAway = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
            setOpen(false)
            setSettingsOpen(false)
        }
    }

    const handleSelect = (idx) => {
        setSelectedTab(idx);
    }

    const handleInputClick = () => {
        setOpen(true)
        setSettingsOpen(false)
    }

    const handleSettingsClick = () => {
        setSettingsOpen(true)
    }

    const handleBackToSearch = () => {
        setSettingsOpen(false)
    }

    const handleTextChange = (event) => {
        const value = event.target.value
        setSearchText(value)
    }

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            handleSearch()
        }
    }

    const handleSearch = () => {
        if (searchText.length <= 0) {
            setAlertNotification("error", intl.formatMessage({ id: "value.to.search" }))
            return
        }
        const validate = validateDateRange()
        if (!validate.valid) {
            setAlertNotification("error", validate.message)
            return
        }
        localStorage.setItem("generalSearchPreferences", JSON.stringify({
            ...searchPreferences,
            searchText: searchText,
            selectedTab: selectedTab,
        }))
        let language = window.location.pathname.split("/")[1];
        if (language != "pt" && language != "es" && language != "en")
            language = "pt"
        history.push(`/${language}/general-search`)
    }

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue)
    }

    const handleSwitchChange = (tab, switchOption, value) => {

        if (switchOption === "startdate" || switchOption === "enddate") {
            PapiroConsole.log(value)
            value = moment(value).format('YYYY/MM/DD HH:mm:ss')
        }

        setSearchPreferences({
            ...searchPreferences,
            [tab]: {
                ...searchPreferences[tab],
                [switchOption]: value
            }
        })
        localStorage.setItem("generalSearchPreferences", JSON.stringify({
            ...searchPreferences,
            [tab]: {
                ...searchPreferences[tab],
                [switchOption]: value
            }
        }))
    }

    useEffect(() => {
        validateDateRange()
    }, [searchPreferences])

    const validateDateRange = () => {
        const { startdate, enddate } = searchPreferences.general
        const startDateMoment = moment(startdate, 'YYYY/MM/DD HH:mm:ss')
        const endDateMoment = moment(enddate, 'YYYY/MM/DD HH:mm:ss')
        const maxDateRange = startDateMoment.clone().add(6, 'months')

        var message = intl.formatMessage({ id: "general.search.max.period" })
        if (startDateMoment.isAfter(endDateMoment)) {
            setAlertType("error")
            message = intl.formatMessage({ id: "report.audit.initial.date.higher.than.end.date" })
            setAlertMessage(message)
            return { valid: false, message: message }
        }
        if (endDateMoment.isAfter(maxDateRange)) {
            setAlertType("error")
            setAlertMessage(message)
            return { valid: false, message: message }
        }
        if (startDateMoment.isAfter(moment())) {
            message = intl.formatMessage({ id: "called.time.entries.registration.date.field" })
            setAlertType("error")
            setAlertMessage(message)
            return { valid: false, message: message }
        }
        setAlertType("info")
        setAlertMessage(intl.formatMessage({ id: "general.search.max.period" }))
        return { valid: true, message: message }
    }

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus()
        }
    }, [open])

    PapiroConsole.log('isSmallScreen')
    PapiroConsole.log(isSmallScreen)
    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box
                ref={containerRef}
                sx={{
                    position: "relative",
                    display: "inline-block",
                    maxWidth: 500,
                    width: "100%",
                    width: open ? (isSmallScreen ? "10px" : "500px") : (isSmallScreen ? "100%" : "250px"),
                    minWidth: isSmallScreen ? "50px" : "240px",
                    maxWidth: "100%",
                    transition: "all 0.1s ease",
                }}
            >
                <TextField
                    fullWidth
                    inputRef={inputRef}
                    placeholder="Busca"
                    onClick={handleInputClick}
                    variant="outlined"
                    size="small"
                    value={searchText}
                    onChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" >
                                <IconButton
                                    onClick={handleSearch}
                                    style={{
                                        color: "var(--mexx-green-primary)",
                                        backgroundColor: "white",
                                    }}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: {
                            bgcolor: open ? "white" : "#f1f3f5",
                            borderRadius: open ? "8px 8px 0 0" : 2,
                            borderColor: open ? "primary.main" : "transparent",
                            transition: "all 0.2s ease-in-out",
                        },
                    }}
                    sx={{
                        bgcolor: open ? "white" : "#f1f3f5",
                        borderRadius: open ? "8px 8px 0 0" : 2,
                        transition: "all 0.2s ease-in-out",
                    }}
                />

                {open && (
                    <Paper
                        elevation={4}
                        sx={{
                            position: "absolute",
                            top: "calc(100% - 1px)",
                            left: 0,
                            right: 0,
                            zIndex: 1300,
                            borderTop: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: "0 0 8px 8px",
                            width: "100%",
                            p: 2,
                            backgroundColor: "background.paper"
                        }}
                    >
                        {settingsOpen ? (
                            <Box display="flex" flexDirection="column" gap={2} >
                                <Box display="flex" alignItems="center" gap={1}>
                                    <IconButton onClick={handleBackToSearch} size="small">
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Typography variant="subtitle1">{intl.formatMessage({ id: "search.preferences" })}</Typography>
                                </Box>
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                                >
                                    {settingsTabs.map((tab, index) => (
                                        <Tab key={index} label={tab.label} />
                                    ))}
                                </Tabs>
                                <Box mt={1} overflow="auto" maxHeight="250px" >
                                    <Grid container direction="column" spacing={1} >
                                        {
                                            activeTab == 0 ?
                                                <Grid item container xs={12} sm={12} md={12} spacing={1}>
                                                    <Grid item xs={11} sm={11} md={11} marginLeft={2} marginTop={1}>
                                                        <FormControlLabel
                                                            control={
                                                                <SimpleDatePicker
                                                                    label={intl.formatMessage({ id: "start.date" })}
                                                                    name='startDate'
                                                                    stateName='startDate'
                                                                    changeDate={(stateName, value) => handleSwitchChange('general', 'startdate', value)}
                                                                    currentDate={searchPreferences.general.startdate}
                                                                    required
                                                                />
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={11} sm={11} md={11} marginLeft={2} >
                                                        <FormControlLabel
                                                            control={
                                                                <SimpleDatePicker
                                                                    label={intl.formatMessage({ id: "end.date" })}
                                                                    name='endDate'
                                                                    stateName='endDate'
                                                                    changeDate={(stateName, value) => handleSwitchChange('general', 'enddate', value)}
                                                                    currentDate={searchPreferences.general.enddate}
                                                                    required
                                                                />
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={12} md={12} >
                                                        <Alert severity={alertType} variant="standard">
                                                            {alertMessage}
                                                        </Alert>
                                                    </Grid>
                                                </Grid>
                                                :
                                                settingsTabs[activeTab].switches.map((s, idx) => {
                                                    switch (s.name) {
                                                        case "quicksearch":
                                                            return (
                                                                <Grid item container xs={12} sm={12} md={12} key={idx}>
                                                                    <Grid item xs={11} sm={11} md={11} >
                                                                        <FormControlLabel
                                                                            key={idx}
                                                                            control={
                                                                                <Switch
                                                                                    defaultChecked={s.label !== "Ticket properties"}
                                                                                    checked={searchPreferences[settingsTabs[activeTab].name][s.name]}
                                                                                    onChange={(event, value) => handleSwitchChange(settingsTabs[activeTab].name, s.name, value)}
                                                                                />
                                                                            }
                                                                            disabled={!usematerializedview}
                                                                            label={s.label}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={1} sm={1} md={1} key={idx}>
                                                                        <Tooltip title={intl.formatMessage({ id: "quick.search.tooltip" })}>
                                                                            <Info />
                                                                        </Tooltip>
                                                                    </Grid>
                                                                    {
                                                                        !searchPreferences.calleds.quicksearch && usematerializedview &&
                                                                        <Grid item xs={12} sm={12} md={12} key={idx}>
                                                                            <Alert severity="warning" variant="standard">
                                                                                {intl.formatMessage({ id: "quick.search.info" })}
                                                                            </Alert>
                                                                        </Grid>
                                                                    }
                                                                </Grid>
                                                            )
                                                        case "description":
                                                            return (
                                                                <Grid item container xs={12} sm={12} md={12} key={idx}>
                                                                    <Grid item xs={11} sm={11} md={11} >
                                                                        <FormControlLabel
                                                                            key={idx}
                                                                            control={
                                                                                <Switch
                                                                                    defaultChecked={s.label !== "Ticket properties"}
                                                                                    checked={searchPreferences[settingsTabs[activeTab].name][s.name]}
                                                                                    onChange={(event, value) => handleSwitchChange(settingsTabs[activeTab].name, s.name, value)}
                                                                                />
                                                                            }
                                                                            label={s.label}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={1} sm={1} md={1} key={idx}>
                                                                        <Tooltip title={intl.formatMessage({ id: "general.search.description.tooltip" })}>
                                                                            <Info />
                                                                        </Tooltip>
                                                                    </Grid>
                                                                </Grid>
                                                            )
                                                        default:
                                                            return (
                                                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                                                    <FormControlLabel
                                                                        key={idx}
                                                                        control={
                                                                            <Switch
                                                                                defaultChecked={s.label !== "Ticket properties"}
                                                                                checked={searchPreferences[settingsTabs[activeTab].name][s.name]}
                                                                                onChange={(event, value) => handleSwitchChange(settingsTabs[activeTab].name, s.name, value)}
                                                                            />
                                                                        }
                                                                        label={s.label}
                                                                    />
                                                                </Grid>
                                                            )
                                                    }
                                                })
                                        }
                                    </Grid>
                                    {settingsTabs[activeTab].switches.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            No preferences available for this section.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box>
                                <Box display="flex" alignItems="start" justifyContent="space-between" flexWrap="nowrap" gap={1} mb={2}>
                                    <Box display="flex" gap={1} flexWrap="wrap" flexGrow={1}>
                                        {tabs.map((label, idx) => (
                                            <Button
                                                key={idx}
                                                variant={selectedTab === idx ? "contained" : "outlined"}
                                                size="small"
                                                //sx={{ borderRadius: 999 }}
                                                onClick={() => handleSelect(idx)}
                                            >
                                                {label}
                                            </Button>
                                        ))}
                                    </Box>
                                    <Box flexShrink={0}>
                                        <IconButton onClick={handleSettingsClick}>
                                            <SettingsIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    )
}

export default GeneralSearchBar